// Filter Gallery Dialog — multi-panel dialog with filter categories,
// live preview, parameter controls, and filter stacking.

import type { Filter } from "../../core/filters/Filter";
import { PixelBuffer } from "../../core/pixel/PixelBuffer";
import {
  galleryFilterEntries,
  getGalleryFiltersByCategory,
  type GalleryFilterEntry,
} from "../../core/filters/GalleryFilters";

export interface GalleryFilterInstance {
  entryId: string;
  params: Record<string, number>;
  enabled: boolean;
}

export interface FilterGalleryResult {
  filters: GalleryFilterInstance[];
}

export class FilterGalleryDialog {
  private overlay: HTMLDivElement | null = null;
  private resolve: ((result: FilterGalleryResult | null) => void) | null = null;
  private instances: GalleryFilterInstance[] = [];
  private selectedInstanceId: string | null = null;
  private sourcePixels: Uint8ClampedArray | null = null;
  private sourceWidth = 0;
  private sourceHeight = 0;
  private previewCanvas: HTMLCanvasElement | null = null;
  private previewCtx: CanvasRenderingContext2D | null = null;
  private filterListEl: HTMLDivElement | null = null;
  private paramPanelEl: HTMLDivElement | null = null;
  private categoryListEl: HTMLDivElement | null = null;
  private selectedCategory = "Artistic";
  private previewScheduled = false;

  open(
    sourcePixels: Uint8ClampedArray,
    width: number,
    height: number,
  ): Promise<FilterGalleryResult | null> {
    this.close();
    this.sourcePixels = new Uint8ClampedArray(sourcePixels);
    this.sourceWidth = width;
    this.sourceHeight = height;
    this.instances = [];
    this.selectedInstanceId = null;

    return new Promise<FilterGalleryResult | null>((resolve) => {
      this.resolve = resolve;

      const overlay = document.createElement("div");
      overlay.className = "filter-dialog";
      // ARIA: modal dialog accessibility (#47)
      overlay.setAttribute("role", "dialog");
      overlay.setAttribute("aria-modal", "true");
      overlay.setAttribute("aria-labelledby", "filter-gallery-dialog-title");
      overlay.innerHTML = `
        <div class="filter-dialog__sheet" style="width:820px;max-height:90vh">
          <div class="filter-dialog__title" id="filter-gallery-dialog-title">Filter Gallery</div>
          <div class="filter-dialog__body" style="display:flex;gap:0;padding:0;min-height:420px">
            <!-- Preview panel -->
            <div style="flex:1;display:flex;flex-direction:column;padding:8px;border-right:1px solid var(--border)">
              <canvas class="filter-gallery__preview" width="380" height="300" style="width:100%;border:1px solid var(--border);background:#333"></canvas>
              <div style="margin-top:4px;display:flex;gap:4px;align-items:center">
                <button class="filter-gallery__zoom-btn" data-zoom="in" style="font-size:11px;padding:2px 8px">+</button>
                <button class="filter-gallery__zoom-btn" data-zoom="out" style="font-size:11px;padding:2px 8px">−</button>
                <span class="filter-gallery__zoom-label" style="font-size:11px;color:var(--text-dim)">Fit</span>
              </div>
            </div>
            <!-- Category + filter list -->
            <div style="width:180px;border-right:1px solid var(--border);display:flex;flex-direction:column">
              <div style="font-size:10px;font-weight:600;color:var(--text-dim);text-transform:uppercase;padding:6px 8px 2px">Categories</div>
              <div class="filter-gallery__categories" style="flex:0 0 auto;max-height:80px;overflow-y:auto;font-size:11px;padding:0 8px"></div>
              <div style="font-size:10px;font-weight:600;color:var(--text-dim);text-transform:uppercase;padding:6px 8px 2px">Filters</div>
              <div class="filter-gallery__filter-list" style="flex:1;overflow-y:auto;font-size:11px;padding:0 8px"></div>
              <div style="padding:4px 8px;border-top:1px solid var(--border)">
                <div style="font-size:10px;font-weight:600;color:var(--text-dim);text-transform:uppercase;margin-bottom:2px">Stack</div>
                <div class="filter-gallery__stack" style="max-height:100px;overflow-y:auto;font-size:11px"></div>
                <select class="filter-gallery__add-select" style="width:100%;background:var(--bg-input);border:1px solid var(--border);color:var(--text);padding:2px 4px;font-size:10px;margin-top:4px">
                  <option value="">+ Add filter...</option>
                </select>
              </div>
            </div>
            <!-- Parameters panel -->
            <div style="width:200px;display:flex;flex-direction:column">
              <div style="font-size:10px;font-weight:600;color:var(--text-dim);text-transform:uppercase;padding:6px 8px 2px">Parameters</div>
              <div class="filter-gallery__params" style="flex:1;overflow-y:auto;padding:4px 8px;font-size:11px"></div>
            </div>
          </div>
          <div class="filter-dialog__actions">
            <button class="filter-dialog__btn filter-dialog__btn--cancel" data-action="cancel">Cancel</button>
            <button class="filter-dialog__btn filter-dialog__btn--primary" data-action="ok">OK</button>
          </div>
        </div>
      `;

      document.body.appendChild(overlay);
      this.overlay = overlay;

      this.previewCanvas = overlay.querySelector<HTMLCanvasElement>(".filter-gallery__preview");
      this.previewCtx = this.previewCanvas?.getContext("2d") ?? null;
      this.filterListEl = overlay.querySelector(".filter-gallery__filter-list");
      this.paramPanelEl = overlay.querySelector(".filter-gallery__params");
      this.categoryListEl = overlay.querySelector(".filter-gallery__categories");

      this.buildCategoryList();
      this.buildFilterList();
      this.buildAddSelect();
      this.renderPreview();
      this.renderStack();

      // Zoom buttons
      let galleryZoom = 1;
      overlay.querySelectorAll<HTMLButtonElement>(".filter-gallery__zoom-btn").forEach((btn) => {
        btn.addEventListener("click", () => {
          if (btn.dataset.zoom === "in") galleryZoom = Math.min(4, galleryZoom * 1.5);
          else galleryZoom = Math.max(0.25, galleryZoom / 1.5);
          const label = overlay.querySelector<HTMLElement>(".filter-gallery__zoom-label");
          if (label) label.textContent = `${Math.round(galleryZoom * 100)}%`;
          this.renderPreview();
        });
      });

      // OK / Cancel
      overlay.querySelector<HTMLButtonElement>('[data-action="ok"]')?.addEventListener("click", () => {
        this.resolve?.({ filters: [...this.instances] });
        this.close();
      });
      overlay.querySelector<HTMLButtonElement>('[data-action="cancel"]')?.addEventListener("click", () => {
        this.resolve?.(null);
        this.close();
      });
      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) { this.resolve?.(null); this.close(); }
      });

      // Escape / Ctrl+Enter
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") { this.resolve?.(null); this.close(); document.removeEventListener("keydown", onKey); }
        if (e.key === "Enter" && e.ctrlKey) { this.resolve?.({ filters: [...this.instances] }); this.close(); document.removeEventListener("keydown", onKey); }
      };
      document.addEventListener("keydown", onKey);
    });
  }

  private buildCategoryList(): void {
    if (!this.categoryListEl) return;
    const categories = getGalleryFiltersByCategory();
    this.categoryListEl.innerHTML = Array.from(categories.keys()).map((cat) =>
      `<div class="filter-gallery__category-item${cat === this.selectedCategory ? " is-active" : ""}" data-category="${cat}" style="padding:2px 4px;cursor:pointer;border-radius:2px">${cat}</div>`
    ).join("");
    this.categoryListEl.querySelectorAll(".filter-gallery__category-item").forEach((el) => {
      el.addEventListener("click", () => {
        this.selectedCategory = (el as HTMLElement).dataset.category!;
        this.buildCategoryList();
        this.buildFilterList();
      });
    });
  }

  private buildFilterList(): void {
    if (!this.filterListEl) return;
    const categories = getGalleryFiltersByCategory();
    const filters = categories.get(this.selectedCategory) || [];
    this.filterListEl.innerHTML = filters.map((f) =>
      `<div class="filter-gallery__filter-item${this.selectedInstanceId === f.id ? " is-active" : ""}" data-id="${f.id}" style="padding:3px 4px;cursor:pointer;border-radius:2px">${f.name}</div>`
    ).join("");
    this.filterListEl.querySelectorAll(".filter-gallery__filter-item").forEach((el) => {
      el.addEventListener("click", () => {
        this.selectedInstanceId = (el as HTMLElement).dataset.id!;
        this.buildFilterList();
        this.buildParamPanel();
        this.schedulePreview();
      });
    });
  }

  private buildAddSelect(): void {
    const overlay = this.overlay;
    if (!overlay) return;
    const select = overlay.querySelector<HTMLSelectElement>(".filter-gallery__add-select");
    if (!select) return;
    const categories = getGalleryFiltersByCategory();
    let html = '<option value="">+ Add filter...</option>';
    for (const [cat, filters] of categories) {
      html += `<optgroup label="${cat}">`;
      for (const f of filters) {
        html += `<option value="${f.id}">${f.name}</option>`;
      }
      html += `</optgroup>`;
    }
    select.innerHTML = html;

    const addBtn = document.createElement("button");
    addBtn.textContent = "Add";
    addBtn.style.cssText = "width:100%;font-size:10px;padding:2px;margin-top:2px";
    addBtn.addEventListener("click", () => {
      const id = select.value;
      if (!id) return;
      const entry = galleryFilterEntries.find(e => e.id === id);
      if (!entry) return;
      const params: Record<string, number> = {};
      for (const def of entry.paramDefs) {
        params[def.key] = def.default;
      }
      this.instances.push({ entryId: id, params, enabled: true });
      this.selectedInstanceId = id;
      select.value = "";
      this.buildFilterList();
      this.buildParamPanel();
      this.renderStack();
      this.schedulePreview();
    });
    select.parentElement?.appendChild(addBtn);
  }

  private buildParamPanel(): void {
    if (!this.paramPanelEl) return;
    const entry = galleryFilterEntries.find(e => e.id === this.selectedInstanceId);
    if (!entry) {
      this.paramPanelEl.innerHTML = '<div style="color:var(--text-dim);padding:8px">Select a filter to adjust parameters</div>';
      return;
    }
    this.paramPanelEl.innerHTML = entry.paramDefs.map((def) => `
      <div class="filter-gallery__param-row" style="margin-bottom:6px">
        <label style="display:block;font-size:10px;color:var(--text-dim);margin-bottom:2px">${def.label}</label>
        <div style="display:flex;gap:4px;align-items:center">
          <input type="range" class="filter-gallery__slider" data-key="${def.key}"
            min="${def.min}" max="${def.max}" step="${def.step ?? 1}" value="${this.getCurrentParam(def.key, def.default)}"
            style="flex:1;height:16px" />
          <input type="number" class="filter-gallery__num" data-key="${def.key}"
            min="${def.min}" max="${def.max}" step="${def.step ?? 1}" value="${this.getCurrentParam(def.key, def.default)}"
            style="width:45px;background:var(--bg-input);border:1px solid var(--border);color:var(--text);padding:1px 3px;font-size:10px" />
        </div>
      </div>
    `).join("");

    this.paramPanelEl.querySelectorAll(".filter-gallery__slider").forEach((slider) => {
      slider.addEventListener("input", () => {
        const key = (slider as HTMLInputElement).dataset.key!;
        const numInput = this.paramPanelEl?.querySelector<HTMLInputElement>(`.filter-gallery__num[data-key="${key}"]`);
        if (numInput) numInput.value = (slider as HTMLInputElement).value;
        this.updateCurrentParam(key, parseFloat((slider as HTMLInputElement).value));
        this.schedulePreview();
      });
    });
    this.paramPanelEl.querySelectorAll(".filter-gallery__num").forEach((num) => {
      num.addEventListener("input", () => {
        const key = (num as HTMLInputElement).dataset.key!;
        const slider = this.paramPanelEl?.querySelector<HTMLInputElement>(`.filter-gallery__slider[data-key="${key}"]`);
        if (slider) slider.value = (num as HTMLInputElement).value;
        this.updateCurrentParam(key, parseFloat((num as HTMLInputElement).value));
        this.schedulePreview();
      });
    });
  }

  private getCurrentParam(key: string, defaultVal: number): number {
    const inst = this.instances.find(i => i.entryId === this.selectedInstanceId);
    return inst ? (inst.params[key] ?? defaultVal) : defaultVal;
  }

  private updateCurrentParam(key: string, value: number): void {
    const inst = this.instances.find(i => i.entryId === this.selectedInstanceId);
    if (inst) inst.params[key] = value;
  }

  private renderStack(): void {
    const overlay = this.overlay;
    if (!overlay) return;
    const stackEl = overlay.querySelector<HTMLElement>(".filter-gallery__stack");
    if (!stackEl) return;
    if (this.instances.length === 0) {
      stackEl.innerHTML = '<div style="color:var(--text-dim);font-size:10px;padding:2px">No filters added</div>';
      return;
    }
    stackEl.innerHTML = this.instances.map((inst, i) => {
      const entry = galleryFilterEntries.find(e => e.id === inst.entryId);
      const name = entry ? entry.name : inst.entryId;
      return `<div class="filter-gallery__stack-item${inst.enabled ? "" : " is-disabled"}" data-idx="${i}" style="padding:2px 0;display:flex;align-items:center;gap:4px">
        <input type="checkbox" ${inst.enabled ? "checked" : ""} data-toggle="${i}" style="width:12px;height:12px">
        <span style="flex:1;cursor:pointer">${name}</span>
        <button data-remove="${i}" style="font-size:10px;padding:0 4px;background:none;border:none;color:var(--text-dim);cursor:pointer">×</button>
      </div>`;
    }).join("");

    stackEl.querySelectorAll("input[type=checkbox]").forEach((cb) => {
      cb.addEventListener("change", () => {
        const idx = parseInt((cb as HTMLInputElement).dataset.toggle!, 10);
        if (this.instances[idx]) this.instances[idx]!.enabled = (cb as HTMLInputElement).checked;
        this.schedulePreview();
      });
    });
    stackEl.querySelectorAll("button[data-remove]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const idx = parseInt((btn as HTMLButtonElement).dataset.remove!, 10);
        this.instances.splice(idx, 1);
        if (this.selectedInstanceId && !this.instances.some(i => i.entryId === this.selectedInstanceId)) {
          this.selectedInstanceId = null;
        }
        this.buildFilterList();
        this.buildParamPanel();
        this.renderStack();
        this.schedulePreview();
      });
    });
    stackEl.querySelectorAll(".filter-gallery__stack-item span").forEach((span) => {
      span.addEventListener("click", () => {
        const idx = parseInt((span.parentElement as HTMLElement).dataset.idx!, 10);
        const inst = this.instances[idx];
        if (inst) {
          this.selectedInstanceId = inst.entryId;
          this.buildFilterList();
          this.buildParamPanel();
        }
      });
    });
  }

  private schedulePreview(): void {
    if (this.previewScheduled) return;
    this.previewScheduled = true;
    requestAnimationFrame(() => {
      this.previewScheduled = false;
      this.renderPreview();
    });
  }

  private renderPreview(): void {
    if (!this.previewCanvas || !this.previewCtx || !this.sourcePixels) return;
    const ctx = this.previewCtx;
    const cw = this.previewCanvas.width;
    const ch = this.previewCanvas.height;

    // Scale source to preview size
    const sw = this.sourceWidth;
    const sh = this.sourceHeight;
    const previewData = ctx.createImageData(cw, ch);

    // Downscale source
    for (let dy = 0; dy < ch; dy++) {
      for (let dx = 0; dx < cw; dx++) {
        const sx = Math.floor(dx * sw / cw);
        const sy = Math.floor(dy * sh / ch);
        const si = (sy * sw + sx) * 4;
        const di = (dy * cw + dx) * 4;
        previewData.data[di] = this.sourcePixels[si]!;
        previewData.data[di + 1] = this.sourcePixels[si + 1]!;
        previewData.data[di + 2] = this.sourcePixels[si + 2]!;
        previewData.data[di + 3] = this.sourcePixels[si + 3]!;
      }
    }

    // Apply enabled filters in sequence
    let currentPixels = new Uint8ClampedArray(previewData.data);
    const pw = cw, ph = ch;

    for (const inst of this.instances) {
      if (!inst.enabled) continue;
      const entry = galleryFilterEntries.find(e => e.id === inst.entryId);
      if (!entry) continue;
      try {
        const buf = new PixelBuffer(pw, ph, "rgba8");
        buf.data.set(currentPixels);
        const result = (entry.filter as Filter<unknown>).apply(buf, inst.params as never, null);
        currentPixels = new Uint8ClampedArray(result.data);
      } catch {
        // Skip filter on error
      }
    }

    // Draw result
    const resultData = ctx.createImageData(cw, ch);
    resultData.data.set(currentPixels);
    ctx.putImageData(resultData, 0, 0);

    // Split line
    ctx.strokeStyle = "rgba(255,255,255,0.3)";
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(cw / 2, 0);
    ctx.lineTo(cw / 2, ch);
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw original on left half
    const origData = ctx.createImageData(Math.floor(cw / 2), ch);
    for (let dy = 0; dy < ch; dy++) {
      for (let dx = 0; dx < Math.floor(cw / 2); dx++) {
        const sx = Math.floor(dx * sw / cw);
        const sy = Math.floor(dy * sh / ch);
        const si = (sy * sw + sx) * 4;
        const di = (dy * Math.floor(cw / 2) + dx) * 4;
        origData.data[di] = this.sourcePixels[si]!;
        origData.data[di + 1] = this.sourcePixels[si + 1]!;
        origData.data[di + 2] = this.sourcePixels[si + 2]!;
        origData.data[di + 3] = this.sourcePixels[si + 3]!;
      }
    }
    ctx.putImageData(origData, 0, 0);

    // Labels
    ctx.font = "10px sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.7)";
    ctx.fillText("Before", 4, 14);
    ctx.fillText("After", cw / 2 + 4, 14);
  }

  private close(): void {
    if (this.overlay && this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
    }
    this.overlay = null;
    this.resolve = null;
    this.previewCanvas = null;
    this.previewCtx = null;
    this.sourcePixels = null;
  }
}
